import type { NormalizedMetrics } from '../measurement/normalize';
import {
  RATING_BANDS,
  SCORE_CAPS,
  SCORE_CURVES,
  SCORE_WEIGHTS,
  type Curve,
  type MetricKey,
  type Rating,
} from './config';

export interface ScoreComponent {
  value: number;
  subScore: number;
  weight: number;
}

export interface HealthScore {
  /** Integer 0–100. */
  score: number;
  rating: Rating;
  components: Partial<Record<MetricKey, ScoreComponent>>;
  /** The metric whose weakness capped the score, if any. */
  cappedBy?: MetricKey;
}

export function interpolate(curve: Curve, x: number): number {
  const first = curve[0];
  const last = curve[curve.length - 1];
  if (!first || !last) throw new Error('empty curve');
  if (x <= first[0]) return first[1];
  if (x >= last[0]) return last[1];
  for (let i = 1; i < curve.length; i++) {
    const [x1, y1] = curve[i]!;
    const [x0, y0] = curve[i - 1]!;
    if (x <= x1) return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
  }
  return last[1];
}

export function ratingFor(score: number): Rating {
  return (RATING_BANDS.find((band) => score >= band.min) ?? RATING_BANDS[RATING_BANDS.length - 1]!).rating;
}

export function metricValues(metrics: NormalizedMetrics): Partial<Record<MetricKey, number>> {
  return {
    download: metrics.downloadMbps,
    upload: metrics.uploadMbps,
    latency: metrics.latencyMs,
    jitter: metrics.jitterMs,
    responsiveness: metrics.latencyIncreaseMs,
  };
}

/** Returns null when a core metric (download, upload, idle latency) is missing: no score without evidence. */
export function scoreMetrics(metrics: NormalizedMetrics): HealthScore | null {
  if (metrics.downloadMbps === undefined || metrics.uploadMbps === undefined || metrics.latencyMs === undefined) {
    return null;
  }
  const components: Partial<Record<MetricKey, ScoreComponent>> = {};
  let weightSum = 0;
  for (const [key, value] of Object.entries(metricValues(metrics)) as [MetricKey, number | undefined][]) {
    if (value === undefined) continue;
    components[key] = { value, subScore: interpolate(SCORE_CURVES[key], value), weight: SCORE_WEIGHTS[key] };
    weightSum += SCORE_WEIGHTS[key];
  }

  const entries = Object.entries(components) as [MetricKey, ScoreComponent][];
  let total = entries.reduce((sum, [, c]) => sum + (c.subScore * c.weight) / weightSum, 0);

  let cappedBy: MetricKey | undefined;
  const weakest = entries.reduce((min, entry) => (entry[1].subScore < min[1].subScore ? entry : min));
  for (const cap of SCORE_CAPS) {
    if (weakest[1].subScore < cap.below && total > cap.maxScore) {
      total = cap.maxScore;
      cappedBy = weakest[0];
      break;
    }
  }

  const score = Math.max(0, Math.min(100, Math.round(total)));
  return { score, rating: ratingFor(score), components, ...(cappedBy ? { cappedBy } : {}) };
}
